import { createContext, useContext, useState } from "react";
import { useGetApi } from "utilFunctions/CustomHooks/useGetAPI";
import {
  BASE_URL,
  SUBJECTIVE_TEST,
  SUBJECTIVE_TEST_SUMMARY,
  SUBJECTIVE_TEST_STUDENT_DETAILS,
  POST_STUDENT_TEST_EVALUATION,
  GENERATE_SUBJECTIVE_PRESIGNED_URL,
  DELETE_EVALUATION_LOCK,
  GET_SUBJECTIVE_TEST_PDF,
} from "utilFunctions/Constants/urlConstants";
import { usePutPostAPI } from "utilFunctions/CustomHooks/usePutPostAPI";
import useDeleteData from "utilFunctions/CustomHooks/useDeleteData";
import { ToastContextDispatch } from "Context/CommonContext";

export const MyBatchContext = createContext<any>(null);

export const MyBatchProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [batchDetail, setBatchDetail] = useState({});
  const [testEvaluationFilter, setTestEvaluationFilter] = useState({});
  const value = {
    batchDetail,
    setBatchDetail,
    testEvaluationFilter,
    setTestEvaluationFilter,
  };
  return (
    <MyBatchContext.Provider value={value}>{children}</MyBatchContext.Provider>
  );
};
export const useMyBatchContext = () => {
  const context = useContext(MyBatchContext);
  if (!context) {
    throw new Error("useMyBatchContext must be used within MyBatchProvider");
  }
  return context;
};

export const useSubjectiveTest = () => {
  const { makeRequest, loading, responseData, error } = useGetApi(
    () => {
      console.log("Subjective test fetched successfully");
    },
    () => {
      console.error("Error fetching subjective test");
    }
  );

  const fetchSubjectiveTest = (
    batchId: string | number,
    testId: string | number,
    cmsTestId: string | number
  ) => {
    const url = `${BASE_URL}${SUBJECTIVE_TEST}?testScheduleId=${testId}&batchId=${batchId}&testId=${cmsTestId}`;
    makeRequest(url);
  };

  return {
    fetchSubjectiveTest,
    loading,
    subjectiveTestData: responseData,
    error,
  };
};

export const useSubjectiveTestSummary = () => {
  const { makeRequest, loading, responseData, error } = useGetApi(
    () => {
      console.log("Subjective test summary fetched successfully");
    },
    () => {
      console.error("Error fetching subjective test summary");
    }
  );

  const fetchSubjectiveTestSummary = (
    batchId: string | number,
    testId: string | number
  ) => {
    const url = `${BASE_URL}${SUBJECTIVE_TEST_SUMMARY}?testScheduleId=${testId}&batchId=${batchId}`;
    makeRequest(url);
  };

  return {
    fetchSubjectiveTestSummary,
    loading,
    subjectiveTestSummaryData: responseData,
    error,
  };
};

export const useSubjectiveStudentTestDetails = () => {
  const { makeRequest, loading, responseData, error } = useGetApi(
    () => {
      console.log("Subjective student test details fetched successfully");
    },
    () => {
      console.error("Error fetching subjective student test details");
    }
  );

  const fetchStudentTestDetails = (
    batchId: string | number,
    testId: string | number
  ) => {
    const url = `${BASE_URL}${SUBJECTIVE_TEST_STUDENT_DETAILS}?batchId=${batchId}&testScheduleId=${testId}`;
    makeRequest(url);
  };

  return {
    fetchStudentTestDetails,
    loading,
    studentTestDetailsData: responseData,
    error,
  };
};

export const useGetTestToken = (onFinish?: (token: string) => void) => {
  const { makeRequest, loading, responseData, error } = useGetApi(
    (data: unknown) => {
      console.log("Token fetched successfully", data);
      onFinish && onFinish(data as string); // Inferred that the token is the response or a field in it
    },
    () => {
      console.error("Error fetching token");
    }
  );

  const fetchToken = (testScheduleId: number, testId: number) => {
    const url = `${BASE_URL}${GET_SUBJECTIVE_TEST_PDF}?testScheduleId=${testScheduleId}&testId=${testId}`;
    makeRequest(url);
  };

  return {
    fetchToken,
    loading,
    token: responseData,
    error,
  };
};

export const useSubjectiveTestDetail = () => {
  const ToastDispatch = useContext(ToastContextDispatch);

  const { makeRequest, loading, responseData, error } = useGetApi(
    () => {
      console.log("Subjective test detail fetched successfully");
    },
    (data) => {
      console.error("Error fetching subjective test detail", data);
      ToastDispatch({
        isOpen: true,
        type: "Error",
        message: data["message"],
      });
    }
  );

  const fetchSubjectiveTestDetail = (url: string, id: string | number) => {
    console.log("id", id);
    makeRequest(url);
  };

  return {
    fetchSubjectiveTestDetail,
    loading,
    testDetail: responseData,
    error,
  };
};

export const useEvaluateStudentTest = (
  onSuccessCallback?: () => void,
  onErrorCallback?: (err: any) => void
) => {
  const ToastDispatch = useContext(ToastContextDispatch);
  const { makeRequest, loading, responseData, error } = usePutPostAPI(
    () => {
      console.log("Student test evaluation submitted successfully");
      if (onSuccessCallback) onSuccessCallback();
    },
    (err) => {
      ToastDispatch({
        isOpen: true,
        type: "Error",
        message: err["response"]["data"]["message"],
      });
      if (onErrorCallback) onErrorCallback(err);
    },
    "POST"
  );

  const evaluateStudentTest = (payload: unknown) => {
    const url = `${BASE_URL}${POST_STUDENT_TEST_EVALUATION}`;
    makeRequest(url, payload);
  };

  return {
    evaluateStudentTest,
    loading,
    evaluationResponse: responseData,
    error,
  };
};

export const useGenerateSubjectivePresignedUrl = (
  onSuccessCallback?: (data: any) => void,
  onErrorCallback?: (err: any) => void
) => {
  const { makeRequest, loading, responseData, error } = usePutPostAPI(
    (data) => {
      console.log("Presigned URL generated successfully", data);
      if (onSuccessCallback) onSuccessCallback(data);
    },
    (err) => {
      console.error("Error generating presigned URL");
      if (onErrorCallback) onErrorCallback(err);
    },
    "POST"
  );

  const generatePresignedUrl = (payload: {
    testScheduleId: number;
    submissionId: number;
    batchId?: number;
    fileName?: string;
  }) => {
    const url = `${BASE_URL}${GENERATE_SUBJECTIVE_PRESIGNED_URL}`;
    makeRequest(url, payload);
  };

  return {
    generatePresignedUrl,
    loading,
    presignedUrlData: responseData,
    error,
  };
};

export const useDeleteEvaluationLock = (
  onSuccessCallback?: () => void,
  onErrorCallback?: (err: any) => void
) => {
  const { makeRequest, loading, responseData, error } = useDeleteData(
    () => {
      console.log("Evaluation lock deleted successfully");
      if (onSuccessCallback) onSuccessCallback();
    },
    (err) => {
      console.error("Error deleting evaluation lock");
      if (onErrorCallback) onErrorCallback(err);
    },
    "DELETE"
  );

  const deleteEvaluationLock = (submissionId: string | number) => {
    const url = `${BASE_URL}${DELETE_EVALUATION_LOCK}/${submissionId}`;
    makeRequest(url);
  };

  return {
    deleteEvaluationLock,
    loading,
    deleteResponse: responseData,
    error,
  };
};
